import React, { useEffect, useMemo } from 'react';
import { ITags, TLanguageIDs } from 'types';
import { trueOrderLanguageIDs } from 'appConstants';
import { LanguageBarItemNew } from 'components/LanguageBarItem/LanguageBarItemNew';

const isLanguageId = (value: string): value is TLanguageIDs => trueOrderLanguageIDs.some((id) => id === value);

interface IProps {
	tags: ITags[];
	selectedLanguage: Nullable<string>;
	IDSelectedCustomerDefaultLanguage: Nullable<string>;
	IDsSelectedCustomerLanguages: string[];
	setSelectedLanguage: (value: Nullable<string>) => void;
}

export const LanguageBar: React.FC<IProps> = ({
	tags,
	selectedLanguage,
	IDSelectedCustomerDefaultLanguage,
	IDsSelectedCustomerLanguages,
	setSelectedLanguage,
}) => {
	const languageIDsWithEmptyTags = useMemo(
		() => tags.filter((el) => el.tags.includes('')).map((el) => el.languageId),
		[tags],
	);

	const sortedLanguages = useMemo(() => {
		const languages = IDsSelectedCustomerLanguages.filter(isLanguageId).sort(
			(a, b) => trueOrderLanguageIDs.indexOf(a) - trueOrderLanguageIDs.indexOf(b),
		);
		return [
			...languages.filter((el) => el === IDSelectedCustomerDefaultLanguage),
			...languages.filter((el) => el !== IDSelectedCustomerDefaultLanguage),
		];
	}, [IDsSelectedCustomerLanguages, IDSelectedCustomerDefaultLanguage]);

	useEffect(() => {
		if (
			IDSelectedCustomerDefaultLanguage &&
			sortedLanguages.some((el) => el === IDSelectedCustomerDefaultLanguage)
		) {
			setSelectedLanguage(IDSelectedCustomerDefaultLanguage);
		} else if (sortedLanguages.length) {
			setSelectedLanguage(sortedLanguages[0]);
		} else {
			setSelectedLanguage(null);
		}
	}, [sortedLanguages.length, IDSelectedCustomerDefaultLanguage]);

	if (!sortedLanguages.length || !tags[0]?.tags.length) {
		return null;
	}

	return (
		<div className="flex flex-wrap gap-4" data-test="LanguageBar">
			{sortedLanguages.map((el) => (
				<LanguageBarItemNew
					key={el}
					languageId={el}
					isActive={selectedLanguage === el}
					isDefault={IDSelectedCustomerDefaultLanguage === el}
					hasError={languageIDsWithEmptyTags.includes(el)}
					onClick={() => setSelectedLanguage(el)}
				/>
			))}
		</div>
	);
};
